import { getExternalReviews } from '@app/services/reviews';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ExternalReview } from '@app/models/reviews';


interface ExternalReviews {
  reviews: ExternalReview[];
  count: number;
  pages: number;
  filtered: number;
  averageRating: number;
  countAll: number;
  countNew: number;
  countNeedResponse: number;
  countWithResponse: number;
  filters: any;
}

export const useRatingsReviews = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeTab, setActiveTab] = useState<'all' | 'new' | 'needs-response' | 'with-responses'>('all');
  const [activeReview, setActiveReview] = useState<string | undefined>(
    undefined
  );
  const filtersForm = useForm({
    defaultValues: {
      platforms: ['all'],
      ratings: ['all'],
      sort: 'newest-reviews',
      start: '',
      end: '',
      search: '',
    },
  });


  const handleLimitChange = (newLimit: number | undefined) => {
    setLimit(newLimit ?? 10);
    setPage(1);
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  // Watch form values for changes
  const startDate = filtersForm.watch('start') as string;
  const endDate = filtersForm.watch('end') as string;
  const platforms = filtersForm.watch('platforms') as string[];
  const ratings = filtersForm.watch('ratings') as string[];
  const sortBy = filtersForm.watch('sort') as string;
  const search = filtersForm.watch('search') as string;

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, platforms, ratings, sortBy, search]);

  const {
    data: externalReviewsData,
    isLoading: isLoadingExternal,
    refetch: refetchExternal,
    error: externalError,
  } = useQuery({
    queryKey: ['external-reviews', startDate, endDate, platforms, ratings, sortBy, activeTab, search, page, limit],
    queryFn: async () => {
      const filters: any = {};

      if (startDate) {
        filters.startDate = startDate;
      }
      if (endDate) {
        filters.endDate = endDate;
      }

      if (activeTab) {
        filters.activeTab = activeTab;
      }

      if (platforms) {
        filters.sourceTypes = platforms;
      }

      if (search) {
        filters.search = search;
      }

      // Add rating filter - map to minRating and maxRating
      if (ratings && !ratings.includes('all') && ratings.length > 0) {
        const numericRatings = ratings
          .map((r) => parseInt(r))
          .filter((r) => !isNaN(r))
          .sort((a, b) => a - b); // Sort from lowest to highest

        if (numericRatings.length > 0) {
          filters.ratings = ratings;
        }
      }

      // Add sort filter
      if (sortBy) {
        switch (sortBy) {
          case 'newest':
            filters.sortBy = 'createTime';
            filters.order = 'desc';
            break;
          case 'oldest':
            filters.sortBy = 'createTime';
            filters.order = 'asc';
            break;
          case 'highest':
            filters.sortBy = 'rating';
            filters.order = 'desc';
            break;
          case 'lowest':
            filters.sortBy = 'rating';
            filters.order = 'asc';
            break;
          case 'shortest':
            filters.sortBy = 'reviewLength';
            filters.order = 'asc';
            break;
          case 'longest':
            filters.sortBy = 'reviewLength';
            filters.order = 'desc';
            break;
          default:
            filters.sortBy = 'createTime';
            filters.order = 'desc';
        }
      }

      filters.page = page;
      filters.limit = limit;

      return getExternalReviews(filters);
    },
    enabled: true,
  });

  const externalData: ExternalReviews = {
    reviews: [
      ...(externalReviewsData?.data?.reviews?.map((review: any) => ({
        id: review?.id || '',
        platform: review?.sourceType,
        sourceType: review?.sourceType,
        firstName: review?.reviewerName?.split(' ')[0] || '',
        lastName: review?.reviewerName?.split(' ').slice(1).join(' ') || '',
        reviewerName: review?.reviewerName || '',
        photoUrl: review?.reviewerPhoto || '',
        rating: review?.rating || 0,
        review: review?.body || '',
        createTime: review?.createTime || '',
        updateTime: review?.updateTime || '',
        reply: review?.replyBody || '',
        replyUpdateTime: review?.replyUpdatedAt || '',
        reviewUrl: review?.reviewUrl || '',
      })) || []),
    ],
    count: externalReviewsData?.data?.total || 0,
    filtered: externalReviewsData?.data?.filtered || 0,
    pages: externalReviewsData?.data?.totalPages || 0,
    averageRating: externalReviewsData?.data?.averageRating || 0,
    countAll: externalReviewsData?.data?.countAll || 0,
    countNew: externalReviewsData?.data?.countNew || 0,
    countNeedResponse: externalReviewsData?.data?.countNeedResponse || 0,
    countWithResponse: externalReviewsData?.data?.countWithResponse || 0,
    filters: externalReviewsData?.data?.filters || 0,
  };

  const handleReviewClick = (id: string) => {
    setActiveReview(id);
  };

  const tabOptions = [
    {
      label: 'All',
      value: 'all',
      active: !activeTab || activeTab === 'all',
      count: externalData?.countAll ?? 0,
      handle: () => handleTabClick('all'),
    },
    {
      label: 'New reviews',
      value: 'new',
      tooltip: 'Reviews from the past 7 days.',
      active: activeTab === 'new',
      count: externalData?.countNew ?? 0,
      handle: () => handleTabClick('new'),
    },
    {
      label: 'Needs response',
      value: 'needs-response',
      active: activeTab === 'needs-response',
      count: externalData?.countNeedResponse ?? 0,
      handle: () => handleTabClick('needs-response'),
    },
    {
      label: 'Reviews with responses',
      value: 'with-responses',
      active: activeTab === 'with-responses',
      count: externalData?.countWithResponse ?? 0,
      handle: () => handleTabClick('with-responses'),
    },
  ];

  const handleTabClick = (value: string) => {
    setActiveTab(value as 'all' | 'new' | 'needs-response' | 'with-responses');
    
    setPage(1);
  };

  return {
    requestData: { data: externalData },
    isLoading: isLoadingExternal,
    refetch: () => {
      refetchExternal();
    },
    error: externalError,
    filtersForm,
    handleReviewClick,
    activeReview,
    activeTab,
    tabOptions,
    handleTabClick,
    page,
    limit,
    handleLimitChange,
    handleNextPage,
    handlePreviousPage,
  };
};
